<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <!-- Wide like Ordrer and Produkter: a ten-column device table does not fit Bootstrap's 1320px container. -->
  <div class="container-fluid mt-4 mb-5" style="max-width:1860px; margin:0 auto">
    <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:1rem">
      <div>
        <h3 class="mb-1">CAGISTA MDM</h3>
        <div style="font-style: italic; color:var(--bo-muted); max-width:680px">
          Administrer POS/KIOSK/KDS-enheter via Android Management API. Vendor-nøytralt på tvers av
          Sunmi/iMin/Elo.
        </div>
        <!-- Dev and production share one backend and one AMAPI enterprise, so this list is the real fleet
             even on localhost. Say so once at the top; the per-row badges are the consequence. -->
        <div v-if="isLocalDev()" class="mdm-devbanner mt-2">
          <font-awesome-icon :icon="['fas', 'lock']" />
          <span>
            Kjører fra <code>localhost</code> mot samme AMAPI-enterprise som produksjon. Enheter merket
            <strong>PROD</strong> er skrivebeskyttet her — data kan leses, men ingen endringer, kommandoer
            eller sletting. Bruk produksjons-Backoffice for det.
          </span>
        </div>
      </div>
      <div v-if="status && status.configured" class="d-flex flex-column align-items-end mdm-header-actions" style="gap:0.5rem">
        <span class="mdm-enterprise">Enterprise <code>{{ status.enterpriseId }}</code></span>
        <div class="d-flex gap-2">
          <button class="bo_btn_secondary" @click="$router.push('/mdmApps')">Apper</button>
          <button class="bo_btn_secondary" @click="$router.push('/mdmPolicies')">Policyer</button>
          <button class="bo_btn" @click="$router.push('/mdmEnroll')">Enroll enhet</button>
        </div>
      </div>
    </div>

    <!-- Not configured -->
    <div v-if="!loading && status && !status.configured" class="alert alert-warning mt-4">
      <strong>Android Management API er ikke konfigurert.</strong>
      Sett tjenestekonto-JSON, enterprise-id og project-id for å aktivere enhetsstyring.
      <div class="mt-2">
        <button class="bo_btn" @click="$router.push('/amapiConfig')">Åpne AMAPI Config</button>
      </div>
    </div>

    <template v-if="!loading && status && status.configured">

      <!-- ===================== Device quota ===================== -->
      <div v-if="quota" class="mdm-quota mt-4">
        <div class="d-flex justify-content-between align-items-baseline mb-1">
          <span class="mdm-quota__label">Enhetskvote
            <span v-if="quota.quotaSource === 'google'" class="mdm-quota__source mdm-quota__source--live"
                  title="Lest live fra Google (Service Usage API)">live</span>
          </span>
          <span class="mdm-quota__count"><strong>{{ quota.enrolled }}</strong> / {{ quota.quota }}
            <span style="color:var(--bo-muted)">({{ quota.utilizationPct }}%)</span></span>
        </div>
        <div class="mdm-quota__bar">
          <div class="mdm-quota__fill" :class="quotaBarClass"
               :style="{ width: Math.min(quota.utilizationPct, 100) + '%' }"></div>
        </div>
        <div v-if="quota.utilizationPct >= 100" class="alert alert-danger mt-2 mb-0 py-2">
          <strong>Enhetskvoten er nådd ({{ quota.enrolled }}/{{ quota.quota }}).</strong>
          Nye enheter kan ikke enrolles før kvoten utvides — søk om produksjonskvote NÅ:
          <a href="https://goo.gle/android-enterprise-response" target="_blank" rel="noopener">goo.gle/android-enterprise-response</a>
          («Responding to a quota limit»).
        </div>
        <div v-else-if="quota.shouldApply" class="alert alert-warning mt-2 mb-0 py-2">
          <strong>Du nærmer deg enhetskvoten ({{ quota.enrolled }}/{{ quota.quota }}, {{ quota.utilizationPct }}%).</strong>
          Start søknad om utvidet produksjonskvote nå (Google bruker ~1–2 uker):
          <a href="https://goo.gle/android-enterprise-response" target="_blank" rel="noopener">goo.gle/android-enterprise-response</a>
          («Responding to a quota limit»).
        </div>
      </div>

      <!-- ===================== MDM summary ===================== -->
      <div class="mdm-stats mt-4 mb-4">
        <div class="mdm-stat mdm-stat--total">
          <div class="mdm-stat__value">{{ mdmSummary.total }}</div>
          <div class="mdm-stat__label">Enheter lastet</div>
        </div>
        <div class="mdm-stat mdm-stat--active">
          <div class="mdm-stat__value">{{ mdmSummary.active }}</div>
          <div class="mdm-stat__label">Aktive</div>
        </div>
        <div class="mdm-stat mdm-stat--prov">
          <div class="mdm-stat__value">{{ mdmSummary.provisioning }}</div>
          <div class="mdm-stat__label">Provisjonering</div>
        </div>
        <div class="mdm-stat mdm-stat--disabled">
          <div class="mdm-stat__value">{{ mdmSummary.disabled }}</div>
          <div class="mdm-stat__label">Deaktivert</div>
        </div>
      </div>

      <!-- ===================== Devices ===================== -->
      <h5>Enheter</h5>
      <div class="d-flex flex-wrap align-items-center gap-2 mb-2 mdm-filters">
        <div class="position-relative" style="flex:1 1 260px; min-width:220px; max-width:360px">
          <input class="form-control" type="text" v-model="deviceSearch"
                 placeholder="Søk navn, merchant, modell, id, policy…" style="padding-right:32px" />
          <a v-if="deviceSearch" class="bo_search-clear" title="Tøm søket" @click="deviceSearch = ''">
            <font-awesome-icon :icon="['fas', 'times']" />
          </a>
        </div>
        <select class="form-select" style="max-width:200px" v-model="stateFilter">
          <option value="">Alle tilstander</option>
          <option v-for="s in deviceStates" :key="s" :value="s">{{ s }}</option>
        </select>
        <span class="text-nowrap" style="color:var(--bo-muted)">{{ filteredDevices.length }} / {{ devices.length }} lastet</span>
        <button class="bo_btn_secondary ms-auto" @click="loadDevices(true)" :disabled="devicesLoading">
          <font-awesome-icon :icon="['fas', 'sync']" /> {{ devicesLoading ? 'Henter…' : 'Oppdater' }}
        </button>
      </div>

      <p v-if="deviceError" class="text-danger mt-2">{{ deviceError }}</p>
      <div v-if="!devicesLoading && devices.length === 0 && !deviceError" class="mt-2" style="color:var(--bo-muted)">
        Ingen enheter ennå. Bruk «Enroll enhet» for å registrere den første.
      </div>
      <div v-else-if="devices.length && filteredDevices.length === 0" class="mt-2" style="color:var(--bo-muted)">
        Ingen enheter samsvarer med søk/filter.
      </div>

      <!-- mdm-devices drives the reflow: below 1200px the rows become cards (see the media queries), two
           across from 992px and one below, so the table never has to be scrolled sideways to reach the
           actions button. table-responsive stays only as a safety net and should never scroll. -->
      <div v-if="filteredDevices.length" class="table-responsive">
        <table class="table table-hover align-middle mdm-devices">
          <thead>
            <!-- Every column but Handlinger sorts on click: first click ascending, second flips. The
                 caret shows which one is active and which way; the inactive ones carry the faint
                 double caret so it is visible they CAN be sorted. Default is Name ascending, which is
                 how a person looks for a device — by what they called it. -->
            <tr>
              <th v-for="col in sortableColumns" :key="col.key" :class="col.thClass" class="mdm-th-sort"
                  role="button" :aria-sort="sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'"
                  @click="sortBy(col.key)">
                {{ col.label }}
                <font-awesome-icon class="mdm-sort-caret" :class="{ 'mdm-sort-caret--active': sortKey === col.key }"
                  :icon="['fas', sortKey !== col.key ? 'sort' : (sortDir === 'asc' ? 'sort-up' : 'sort-down')]" />
              </th>
              <!-- The column holds one 34px button; a visible «Handlinger» label made it three times that. -->
              <th class="text-end mdm-col-actions"><span class="visually-hidden">Handlinger</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in sortedDevices" :key="d.id">
              <td class="text-center mdm-col-online" data-label="Online">
                <font-awesome-icon :icon="['fas', 'circle']" class="mdm-light"
                  :class="deviceOnline(d) ? 'mdm-light--online' : 'mdm-light--offline'"
                  :title="(deviceOnline(d) ? 'Online' : 'Offline') + ' · ' + (d.lastHeartbeatMs > 0 ? 'heartbeat' : 'AMAPI-innsjekk') + ': ' + formatTime(lastSeenMs(d))" />
              </td>
              <td data-label="Navn">
                <div v-if="editNameId === d.id" class="d-inline-flex align-items-center gap-1">
                  <input v-model="editNameValue" class="form-control form-control-sm" style="max-width:170px"
                         placeholder="Enhetsnavn" @keyup.enter="saveName(d)" @keyup.esc="editNameId = null" />
                  <button class="mdm-icon-btn" title="Lagre" @click="saveName(d)"><font-awesome-icon :icon="['fas', 'check']" /></button>
                  <button class="mdm-icon-btn" title="Avbryt" @click="editNameId = null"><font-awesome-icon :icon="['fas', 'times']" /></button>
                </div>
                <template v-else>
                  <span :class="{ 'text-muted fst-italic': !d.customName }">{{ d.customName || 'Uten navn' }}</span>
                  <span v-if="isDeviceLocked(d)" class="mdm-prodlock"
                        title="PROD-enhet, og Backoffice kjører fra localhost — bare lesetilgang">
                    <font-awesome-icon :icon="['fas', 'lock']" /> PROD
                  </span>
                  <button v-else class="mdm-name-edit" title="Gi nytt navn" @click="startRename(d)">
                    <font-awesome-icon :icon="['fas', 'pencil-alt']" />
                  </button>
                </template>
              </td>
              <td data-label="Merchant"><code v-if="d.enrollmentTokenData">{{ d.enrollmentTokenData }}</code><span v-else style="color:#adb5bd">—</span></td>
              <td data-label="Modell">{{ deviceModel(d) }}<br/><code class="mdm-device-id">{{ d.id }}</code></td>
              <td data-label="Tilstand">
                <span class="badge" :class="stateClass(d.state)">{{ d.state || '—' }}</span>
                <span v-if="d.rma" class="badge bg-danger ms-1" :title="d.rma.note || 'Merket som RMA'">RMA</span>
              </td>
              <td data-label="Policy">
                <div v-if="editPolicyId === d.id" class="d-inline-flex align-items-center gap-1">
                  <select v-model="editPolicyValue" class="form-select form-select-sm" style="max-width:150px">
                    <option v-for="p in policyOptions" :key="p" :value="p">{{ p }}</option>
                  </select>
                  <button class="mdm-icon-btn" title="Lagre" @click="savePolicy(d)"><font-awesome-icon :icon="['fas', 'check']" /></button>
                  <button class="mdm-icon-btn" title="Avbryt" @click="editPolicyId = null"><font-awesome-icon :icon="['fas', 'times']" /></button>
                </div>
                <template v-else>
                  <!-- Policy names are one long word to a browser (POS_DESKTOP_MAINTENANCE), so they may break
                       after an underscore. The spans after it are inline-block because Vue drops the newline
                       between the tags: without that, «(vedlikehold)» was glued to the name and the pair
                       could not wrap at all. -->
                  <code><template v-for="(part, i) in breakable(shortName(d.appliedPolicyName || d.policyName, 'policies') || '—')" :key="i">{{ part }}<wbr /></template></code>
                  <span v-if="policyPending(d)" class="text-muted ms-1 d-inline-block" style="font-size:0.78rem"
                        title="Ny policy er tildelt – enheten har ikke synket/rapportert den ennå">
                    → <code><template v-for="(part, i) in breakable(shortName(d.policyName, 'policies'))" :key="i">{{ part }}<wbr /></template></code>
                  </span>
                  <button v-if="!deviceOnMaintenance(d) && !isDeviceLocked(d)" class="mdm-name-edit" title="Endre policy" @click="startEditPolicy(d)">
                    <font-awesome-icon :icon="['fas', 'pencil-alt']" />
                  </button>
                  <!-- A _PROV device is mid-provisioning. Normally that clears itself in 20-35 min; if an app
                       cannot install at all it never does, and until now the two looked identical. -->
                  <div v-if="provisioningWait(d)" class="mdm-prov" :class="{ 'mdm-prov--stuck': provisioningWait(d).stuck }">
                    <font-awesome-icon :icon="['fas', provisioningWait(d).stuck ? 'exclamation-triangle' : 'clock']" />
                    <span v-if="provisioningWait(d).packages.length">
                      Venter på installasjon:
                      <code v-for="pkg in provisioningWait(d).packages" :key="pkg" class="mdm-prov__pkg"><template v-for="(part, i) in breakable(pkg)" :key="i">{{ part }}<wbr /></template></code>
                    </span>
                    <span v-else>Alle apper installert — bytter til {{ provisioningWait(d).steady }} ved neste sjekk (hvert 2,5 min).</span>
                  </div>
                  <!-- Spell the condition out. This was a bare v-else on the edit pencil above until the
                       provisioning block was inserted between them, which silently re-bound it to that
                       v-if — so every device that was not mid-provisioning claimed to be in maintenance. -->
                  <span v-else-if="deviceOnMaintenance(d)" class="text-muted fst-italic ms-1 d-inline-block" style="font-size:0.78rem"
                        title="Bruk «Aktiver kiosk-modus» for å låse enheten tilbake til kiosk">(vedlikehold)</span>
                </template>
              </td>
              <td data-label="Android">{{ (d.softwareInfo && d.softwareInfo.androidVersion) || '—' }}</td>
              <td data-label="Sikkerhetspatch">{{ formatDate(d.softwareInfo && d.softwareInfo.securityPatchLevel) }}</td>
              <td data-label="Siste kontakt">
                <span class="fw-semibold" :class="deviceOnline(d) ? 'mdm-light--online' : 'mdm-light--offline'"
                      :title="formatTime(lastSeenMs(d))">{{ relativeTime(lastSeenMs(d)) }}</span>
              </td>
              <td class="text-end mdm-col-actions" data-label="">
                <button class="mdm-icon-btn" title="Handlinger" @click.stop="toggleMenu(d, $event)">
                  <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="devicePageToken" class="mt-2">
        <button class="bo_btn_secondary" @click="loadMoreDevices" :disabled="devicesLoading">
          {{ devicesLoading ? 'Henter…' : 'Last inn flere' }}
        </button>
      </div>
    </template>
  </div>

  <!-- Row-actions dropdown. Fixed-positioned (portal style) so the table's overflow never clips it. -->
  <template v-if="openMenu">
    <div class="mdm-actions-backdrop" @click="openMenu = null"></div>
    <div class="mdm-actions-portal" :style="{ top: openMenu.top + 'px', right: openMenu.right + 'px',
                     maxHeight: 'calc(100vh - ' + (openMenu.top + 12) + 'px)' }">
      <a v-if="remoteAllowed(openMenu.device)" class="mdm-actions__item" @click="menuAction('remote')">Fjern-styring (RustDesk)</a>
      <a v-if="remoteAllowed(openMenu.device)" class="mdm-actions__item" @click="menuAction('rustdeskId')">RustDesk-ID …</a>
      <a class="mdm-actions__item" @click="menuAction('reboot')">Reboot</a>
      <a class="mdm-actions__item" @click="menuAction('lock')">Slå av skjerm</a>
      <a v-if="deviceOnMaintenance(openMenu.device)" class="mdm-actions__item" @click="menuAction('kioskOn')">Aktiver kiosk-modus</a>
      <a v-else class="mdm-actions__item" @click="menuAction('kioskOff')">Avslutt kiosk-modus (vedlikehold)</a>
      <a class="mdm-actions__item" @click="menuAction('details')">Detaljer</a>
      <a class="mdm-actions__item" @click="menuAction('apps')">Installerte apper</a>
      <a class="mdm-actions__item" @click="menuAction('raw')">Vis rå (AMAPI)</a>
      <a class="mdm-actions__item" @click="menuAction('rma')">{{ openMenu.device.rma ? 'RMA-merking…' : 'Merk som RMA' }}</a>
      <!-- One entry per updatable CAGISTA app on THIS device, with the version it runs now. Listing them
           explicitly avoids an extra dialog and makes it impossible to force the wrong app by mistake.
           The launcher is excluded: blocking the home app would leave the device without a home screen
           (the server refuses it too). -->
      <template v-if="updatableApps(openMenu.device).length">
        <div class="mdm-actions__divider"></div>
        <a v-for="app in updatableApps(openMenu.device)" :key="app.packageName"
           class="mdm-actions__item" @click="menuAction('forceUpdate:' + app.packageName)">
          Tving oppdatering: {{ app.displayName }}<template v-if="app.versionCode"> ({{ app.versionCode }})</template>
        </a>
      </template>
      <div class="mdm-actions__divider"></div>
      <a class="mdm-actions__item mdm-actions__item--danger" @click="menuAction('wipe')">Fabrikkgjenoppretting (wipe)</a>
    </div>
  </template>

  <ConfirmDialogue ref="confirmDialog" />

  <popup-modal ref="rawModal">
    <h5 class="mb-1">Rå AMAPI-respons</h5>
    <div style="color:var(--bo-muted); font-size:0.85rem" class="mb-2">{{ rawTitle }}</div>
    <pre class="mdm-raw">{{ rawJson }}</pre>
    <div class="d-flex justify-content-end gap-2 mt-2">
      <button class="bo_btn_secondary" @click="copyRaw">{{ copied ? 'Kopiert ✓' : 'Kopier' }}</button>
      <button class="bo_btn" @click="$refs.rawModal.close()">Lukk</button>
    </div>
  </popup-modal>

  <popup-modal ref="detailsModal">
    <h5 class="mb-1">Enhetsdetaljer</h5>
    <div style="color:var(--bo-muted); font-size:0.85rem" class="mb-2">{{ detailsTitle }}</div>
    <div v-if="detailsDevice" class="mdm-details" style="max-height:60vh; overflow:auto">
      <div class="mdm-details__section">Identitet</div>
      <div class="mdm-details__row"><span>Navn</span><span>{{ detailsDevice.customName || '—' }}</span></div>
      <div class="mdm-details__row"><span>Enhets-ID</span><span><code>{{ detailsDevice.id }}</code></span></div>
      <div class="mdm-details__row"><span>Merchant</span><span>{{ detailsDevice.enrollmentTokenData || '—' }}</span></div>
      <div class="mdm-details__row"><span>Serienummer</span><span><code>{{ hw('serialNumber') || '—' }}</code></span></div>

      <div class="mdm-details__section">Maskinvare</div>
      <div class="mdm-details__row"><span>Produsent</span><span>{{ hw('brand') || hw('manufacturer') || '—' }}</span></div>
      <div class="mdm-details__row"><span>Modell</span><span>{{ hw('model') || '—' }}</span></div>
      <div class="mdm-details__row"><span>Maskinvare</span><span>{{ hw('hardware') || '—' }}</span></div>

      <div class="mdm-details__section">Operativsystem</div>
      <div class="mdm-details__row"><span>Android</span><span>{{ sw('androidVersion') || '—' }}</span></div>
      <div class="mdm-details__row"><span>Build</span><span>{{ sw('androidBuildNumber') || '—' }}</span></div>
      <div class="mdm-details__row"><span>Kernel</span><span>{{ sw('deviceKernelVersion') || '—' }}</span></div>
      <div class="mdm-details__row"><span>Sikkerhetspatch</span><span>{{ formatDate(sw('securityPatchLevel')) }}</span></div>
      <div class="mdm-details__row"><span>Bootloader</span><span>{{ sw('bootloaderVersion') || '—' }}</span></div>

      <div class="mdm-details__section">Tilstand</div>
      <div class="mdm-details__row"><span>Tilstand</span><span>{{ detailsDevice.state || '—' }}</span></div>
      <div class="mdm-details__row"><span>Policy</span><span><code>{{ shortName(detailsDevice.appliedPolicyName || detailsDevice.policyName, 'policies') || '—' }}</code></span></div>
      <div v-if="provisioningWait(detailsDevice)" class="mdm-details__row">
        <span>Provisjonering</span>
        <span v-if="provisioningWait(detailsDevice).packages.length">
          Venter på <code>{{ provisioningWait(detailsDevice).packages.join(', ') }}</code>.
          Enheten blir stående på <code>{{ shortName(detailsDevice.policyName, 'policies') }}</code> til appen er
          installert — den byttes aldri automatisk uten. Er appen ikke tilgjengelig for denne modellen i managed
          Google Play, må du bytte policy manuelt til <code>{{ provisioningWait(detailsDevice).steady }}</code>.
        </span>
        <span v-else>Alle apper installert; bytter til <code>{{ provisioningWait(detailsDevice).steady }}</code> ved neste sjekk.</span>
      </div>
      <div class="mdm-details__row"><span>Online</span><span>{{ deviceOnline(detailsDevice) ? 'Online' : 'Offline' }}</span></div>

      <div class="mdm-details__section">Nettverk</div>
      <div class="mdm-details__row"><span>Wi-Fi MAC</span><span>{{ net('wifiMacAddress') || '—' }}</span></div>
      <div class="mdm-details__row"><span>Operatør</span><span>{{ net('networkOperatorName') || '—' }}</span></div>
      <div class="mdm-details__row"><span>IMEI</span><span>{{ net('imei') || '—' }}</span></div>

      <div class="mdm-details__section">Minne</div>
      <div class="mdm-details__row"><span>RAM</span><span>{{ formatBytes(mem('totalRam')) }}</span></div>
      <div class="mdm-details__row"><span>Lagring</span><span>{{ formatBytes(mem('totalInternalStorage')) }}</span></div>

      <div class="mdm-details__section">Tidspunkter</div>
      <div class="mdm-details__row"><span>Enrollet</span><span>{{ formatTime(detailsDevice.enrollmentTime) }}</span></div>
      <div class="mdm-details__row"><span>Sist policy-synk</span><span>{{ formatTime(detailsDevice.lastPolicySyncTime) }}</span></div>
      <div class="mdm-details__row"><span>Siste kontakt</span><span>{{ formatTime(lastSeenMs(detailsDevice)) }}</span></div>
    </div>
    <div class="d-flex justify-content-end gap-2 mt-3">
      <button class="bo_btn_secondary" @click="detailsToRaw">Vis rå</button>
      <button class="bo_btn" @click="$refs.detailsModal.close()">Lukk</button>
    </div>
  </popup-modal>

  <popup-modal ref="appsModal">
    <div class="mdm-apps-modal">
    <h5 class="mb-1">Installerte apper</h5>
    <div style="color:var(--bo-muted); font-size:0.85rem">{{ appsTitle }}</div>
    <!-- This list is the device's LAST REPORT to AMAPI, not its current state. Say so: after a forced
         update the app is back on the device long before the next check-in, and a stale version number
         here reads as a failed update. -->
    <div v-if="appsDevice" class="mb-2" style="color:#8A6800; font-size:0.85rem">
      Rapportert {{ appsReportedMs(appsDevice) ? relativeTime(appsReportedMs(appsDevice)) : "—" }}. Enheten
      kan allerede ha nyere versjoner enn det som står her; listen oppdateres først ved neste innsjekk.
    </div>

    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
      <div class="position-relative" style="flex:1 1 220px; min-width:180px">
        <input class="form-control" type="text" v-model="appsSearch"
               placeholder="Søk app eller pakkenavn…" style="padding-right:32px" />
        <a v-if="appsSearch" class="bo_search-clear" title="Tøm søket" @click="appsSearch = ''">
          <font-awesome-icon :icon="['fas', 'times']" />
        </a>
      </div>
      <fieldset class="mb-0">
        <label class="multiple-checkbox-label mb-0">
          <input class="multiple-checkbox" type="checkbox" v-model="appsShowSystem" /> Vis systemapper
        </label>
      </fieldset>
      <span class="text-nowrap ms-auto" style="color:var(--bo-muted); font-size:0.85rem">{{ appsList.length }} / {{ appsTotal }} apper</span>
    </div>

    <div v-if="!appsTotal" class="mt-2" style="color:var(--bo-muted)">
      Enheten har ikke rapportert apper ennå — det kan ta noen minutter etter enrollment / neste innsjekk.
    </div>
    <div v-else-if="!appsList.length" class="mt-2" style="color:var(--bo-muted)">Ingen apper samsvarer med søket.</div>

    <div v-else class="table-responsive" style="max-height:56vh; overflow:auto">
      <table class="table table-hover align-middle mdm-apps-table">
        <thead>
          <tr><th>App</th><th>Versjon</th><th>Kilde</th><th>Status</th></tr>
        </thead>
        <tbody>
          <tr v-for="a in appsList" :key="a.packageName">
            <td>
              <span v-if="a.ours" class="mdm-app-ours" title="Distribuert av CAGISTA via Managed Play">CAGISTA</span>
              <span class="fw-semibold">{{ a.displayName }}</span><br/>
              <code class="mdm-device-id">{{ a.packageName }}</code>
            </td>
            <td>{{ a.versionName || '—' }}<span v-if="a.versionCode" style="color:var(--bo-muted-2)"> ({{ a.versionCode }})</span></td>
            <td>{{ appSourceLabel(a.source) }}</td>
            <td>
              <span class="badge" :class="a.state === 'INSTALLED' ? 'bg-success' : 'bg-secondary'">
                {{ a.state === 'INSTALLED' ? 'Installert' : (a.state === 'REMOVED' ? 'Fjernet' : (a.state || '—')) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex justify-content-end gap-2 mt-3">
      <button class="bo_btn_secondary" @click="appsToRaw">Vis rå</button>
      <button class="bo_btn" @click="$refs.appsModal.close()">Lukk</button>
    </div>
    </div>
  </popup-modal>

  <popup-modal ref="rmaModal">
    <h5 class="mb-1">Merk som RMA</h5>
    <div style="color:var(--bo-muted); font-size:0.85rem" class="mb-2">
      Merker enheten som RMA (retur) — et notat. Enheten <strong>slettes ikke</strong> fra AMAPI.
    </div>
    <div v-if="rmaDevice" style="color:var(--bo-muted); font-size:0.85rem" class="mb-2">
      {{ deviceModel(rmaDevice) }} (<code>{{ rmaDevice.id }}</code>)
    </div>
    <label class="form-label mb-1" style="font-size:0.85rem; color:var(--bo-muted)">Notat (valgfritt)</label>
    <textarea class="form-control" rows="3" v-model="rmaNote" placeholder="F.eks. skjerm sprukket, sendt i retur…"></textarea>
    <div class="d-flex justify-content-between align-items-center mt-3">
      <button class="bo_btn_secondary" @click="$refs.rmaModal.close()">Avbryt</button>
      <div class="d-flex gap-2">
        <button v-if="rmaDevice && rmaDevice.rma" class="bo_btn_secondary" @click="clearRma" :disabled="rmaSaving">Fjern RMA-merking</button>
        <button class="bo_btn" @click="saveRma" :disabled="rmaSaving">{{ rmaSaving ? 'Lagrer…' : 'Merk som RMA' }}</button>
      </div>
    </div>
  </popup-modal>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import PopupModal from "@/components/other/PopupModal";
import MdmService from "@/services/system/MdmService";
import TextUtil from "@/util/TextUtil";
import { isLocalDev } from "@/util/MdmLocalGuard";

export default {
  name: "MdmConsole",
  components: { LoadingSpinner, ConfirmDialogue, PopupModal },
  data() {
    return {
      loading: true,
      status: null,
      // Devices (paged — there can be thousands of devices, so we never pull them all at once).
      devices: [],
      // Sort over the loaded devices. Name ascending by default (founder's call, 2026-09-22).
      sortKey: "name",
      sortDir: "asc",
      sortableColumns: [
        { key: "online", label: "Online", thClass: "text-center mdm-col-online" },
        { key: "name", label: "Name" },
        { key: "merchant", label: "Merchant" },
        { key: "model", label: "Modell" },
        { key: "state", label: "Tilstand" },
        { key: "policy", label: "Policy" },
        { key: "android", label: "Android" },
        { key: "patch", label: "Sikkerhetspatch" },
        { key: "lastSeen", label: "Siste kontakt" }
      ],
      devicesLoading: false,
      deviceError: "",
      devicePageSize: 50,
      devicePageToken: null,
      deviceSearch: "",
      stateFilter: "",
      quota: null,
      // Raw-JSON inspect modal (read-only debug view of the AMAPI response for a device).
      rawJson: "",
      rawTitle: "",
      // Formatted device-details modal (reads the existing device object; no extra AMAPI call).
      detailsDevice: null,
      detailsTitle: "",
      // Installed-apps modal (reads the device's AMAPI applicationReports[]; no extra call).
      appsDevice: null,
      appsTitle: "",
      appsSearch: "",
      appsShowSystem: false,
      // RMA mark modal (a note only; the device is NOT deleted from AMAPI).
      rmaDevice: null,
      rmaNote: "",
      rmaSaving: false,
      copied: false,
      // Row-actions dropdown: {device, top, right} of the currently open menu (fixed-positioned), or null.
      openMenu: null,
      // Fallback window for devices with NO order-sync heartbeat yet: online if the AMAPI check-in
      // (status report / policy sync) is within this many minutes. Approximate — AMAPI isn't real-time.
      onlineThresholdMinutes: 15,
      // Primary window: with a real-time order-sync heartbeat present, online = seen within this many
      // seconds (~3× the ~30s SyncService cadence).
      heartbeatOnlineSeconds: 90,
      // Inline device-name rename: the device id being renamed + its working value.
      editNameId: null,
      editNameValue: "",
      // Inline policy change: the device id being edited, its working value, and the available policies.
      editPolicyId: null,
      editPolicyValue: "",
      policyOptions: [],
      policyApps: {},          // policy short name -> [com.cagista.* package] it force-installs
      policyPackages: {},      // policy short name -> EVERY package it installs (the CAGISTA badge in «Installerte apper»)
      // name -> kind (MULTI_APP | SINGLE_APP | MAINTENANCE). Drives kiosk-off detection: a device on ANY
      // maintenance-kind policy is "kiosk off", regardless of the policy's (freely chosen) name.
      policyKinds: {},
      // Remote control (RustDesk) — config-gated; comes from the AMAPI status endpoint.
      remoteControl: { enabled: false, connectUrlTemplate: "", allowedRoles: [] }
    };
  },
  computed: {
    // Counts over the loaded devices (the devices at-a-glance; "load more" extends the sample).
    mdmSummary() {
      const s = { total: this.devices.length, active: 0, provisioning: 0, disabled: 0 };
      for (const d of this.devices) {
        if (d.state === "ACTIVE") s.active++;
        else if (d.state === "PROVISIONING") s.provisioning++;
        else if (d.state === "DISABLED") s.disabled++;
      }
      return s;
    },
    deviceStates() {
      return [...new Set(this.devices.map((d) => d.state).filter(Boolean))];
    },
    quotaBarClass() {
      if (!this.quota) return "";
      const p = this.quota.utilizationPct;
      if (p >= 100) return "mdm-quota__fill--danger";
      if (p >= this.quota.warnThresholdPct) return "mdm-quota__fill--warn";
      return "mdm-quota__fill--ok";
    },
    // Client-side search (multi-term, all terms must match) + state filter over loaded devices.
    filteredDevices() {
      let list = this.devices;
      if (this.stateFilter) list = list.filter((d) => d.state === this.stateFilter);
      const q = this.deviceSearch.trim().toLowerCase();
      if (q) {
        const terms = q.split(/\s+/);
        list = list.filter((d) => {
          const hay = [
            d.customName, d.enrollmentTokenData, this.deviceModel(d), d.id, this.shortName(d.appliedPolicyName || d.policyName, "policies"), d.state,
            d.rma ? "rma " + (d.rma.note || "") : ""
          ].filter(Boolean).join(" ").toLowerCase();
          return terms.every((t) => hay.includes(t));
        });
      }
      return list;
    },
    /**
     * filteredDevices in the chosen order. Sorts on the SAME derivation each column displays —
     * deviceModel(), the shortened policy name, lastSeenMs() — so what you see is what you sorted by;
     * sorting on the raw field would put "Ukjent modell" and "—" in surprising places.
     *
     * Devices with nothing in the sorted column go LAST whichever way the sort runs. «Uten navn» at the
     * top of an ascending list is exactly the row nobody was looking for.
     */
    sortedDevices() {
      const key = this.sortKey;
      const dir = this.sortDir === "desc" ? -1 : 1;
      const collator = new Intl.Collator("nb", { numeric: true, sensitivity: "base" });
      const valueOf = (d) => {
        switch (key) {
          case "online": return this.deviceOnline(d) ? 1 : 0;
          case "name": return d.customName || "";
          case "merchant": return d.enrollmentTokenData || "";
          case "model": return this.deviceModel(d) === "Ukjent modell" ? "" : this.deviceModel(d);
          case "state": return d.state || "";
          case "policy": return this.shortName(d.appliedPolicyName || d.policyName, "policies") || "";
          case "android": return (d.softwareInfo && d.softwareInfo.androidVersion) || "";
          case "patch": return (d.softwareInfo && d.softwareInfo.securityPatchLevel) || "";
          case "lastSeen": return this.lastSeenMs(d) || 0;
          default: return "";
        }
      };
      const empty = (v) => v === "" || v === 0 || v === null || v === undefined;
      // Index as a tiebreak keeps the sort stable across re-renders, so equal rows do not shuffle.
      return this.filteredDevices
        .map((d, i) => ({ d, i, v: valueOf(d) }))
        .sort((a, b) => {
          const ae = empty(a.v), be = empty(b.v);
          if (ae !== be) return ae ? 1 : -1;
          if (ae && be) return a.i - b.i;
          const c = typeof a.v === "number" && typeof b.v === "number"
            ? a.v - b.v
            : collator.compare(String(a.v), String(b.v));
          return c !== 0 ? c * dir : a.i - b.i;
        })
        .map((x) => x.d);
    },
    // Total apps the device has reported (before search / system-app filtering).
    appsTotal() {
      const r = this.appsDevice && this.appsDevice.applicationReports;
      return Array.isArray(r) ? r.length : 0;
    },
    // Installed apps for the apps-modal: CAGISTA-distributed apps pinned first, then user-facing, then
    // A–Z. System (non-user-facing) apps are hidden unless "Vis systemapper" is on; ours always show.
    //
    // «Ours» = every app the device's policy installs through Managed Play — TastyPOS as much as Softpay,
    // Viva or RustDesk — because that is the set CAGISTA put there and is answerable for. Read off the
    // policy rather than a hand-kept list, so adding an app to a policy marks it here on its own. The
    // short list below is the fallback for a device whose policy is not among the ones fetched.
    appsList() {
      const reports = (this.appsDevice && Array.isArray(this.appsDevice.applicationReports))
        ? this.appsDevice.applicationReports : [];
      const OURS = [
        "com.cagista.tastypos", "com.cagista.tastykiosk", "com.cagista.tastykds", "com.cagista.tastyods",
        "com.cagista.tastylauncher", "com.cagista.backoffice", "com.vivawallet.spoc.payapp",
        "com.carriez.flutter_hbb"
      ];
      const policy = this.appsDevice
        ? this.shortName(this.appsDevice.appliedPolicyName || this.appsDevice.policyName, "policies") : "";
      const distributed = this.policyPackages[policy] || [];
      let rows = reports.map((a) => {
        const pkg = a.packageName || "";
        return {
          packageName: pkg,
          displayName: a.displayName || pkg,
          versionName: a.versionName || "",
          versionCode: a.versionCode || "",
          state: a.state || "",
          source: a.applicationSource || "",
          userFacing: a.userFacingType === "USER_FACING",
          ours: OURS.includes(pkg) || distributed.includes(pkg)
        };
      });
      if (!this.appsShowSystem) rows = rows.filter((r) => r.userFacing || r.ours);
      const q = this.appsSearch.trim().toLowerCase();
      if (q) rows = rows.filter((r) =>
        r.displayName.toLowerCase().includes(q) || r.packageName.toLowerCase().includes(q));
      rows.sort((a, b) => {
        if (a.ours !== b.ours) return a.ours ? -1 : 1;
        if (a.userFacing !== b.userFacing) return a.userFacing ? -1 : 1;
        return a.displayName.localeCompare(b.displayName, "nb");
      });
      return rows;
    }
  },
  mounted() {
    MdmService.getStatus().then(
      (response) => {
        this.status = response.data;
        this.remoteControl = response.data.remoteControl || this.remoteControl;
        this.loading = false;
        if (this.status.configured) {
          this.loadDevices(true);
          this.loadQuota();
          this.loadPolicyOptions();
        }
      },
      (error) => {
        console.log(error);
        this.status = { configured: false };
        this.loading = false;
      }
    );
  },
  methods: {
    // Kiosk on/off per device: a device on ANY MAINTENANCE-kind policy (e.g. POS_DESKTOP_MAINTENANCE) is "kiosk off"
    // (unlocked). Detected by the policy's KIND (policyKinds), NOT a literal name — maintenance policies are named
    // freely now (blank-start), so a name match would miss POS_DESKTOP_MAINTENANCE etc.
    deviceOnMaintenance(d) {
      if (!d) return false;
      const name = this.shortName(d.policyName || d.appliedPolicyName, "policies");
      return this.policyKinds[name] === "MAINTENANCE";
    },
    // The device is ASSIGNED a policy it hasn't applied/reported yet (AMAPI status lag, up to ~35 min observed) —
    // e.g. right after «Avslutt kiosk-modus», a policy reassignment, or the _PROV -> steady swap. The row then
    // shows «applied → assigned» so the pending transition is visible instead of looking like a failed switch.
    policyPending(d) {
      if (!d) return false;
      const applied = this.shortName(d.appliedPolicyName, "policies");
      const target = this.shortName(d.policyName, "policies");
      return !!applied && !!target && applied !== target;
    },
    async kioskMode(d, enabled) {
      // enabled=false -> exit kiosk (remember the current kiosk policy, move to MAINTENANCE); true -> re-enter.
      const ok = await this.$refs.confirmDialog.show(
        enabled
          ? {
              title: "Aktiver kiosk-modus",
              message: "Låse denne enheten tilbake til kiosk-modus?" + this.deviceSummary(d),
              okButton: "Aktiver kiosk-modus", cancelButton: "Avbryt"
            }
          : {
              title: "Avslutt kiosk-modus",
              message: "Ta denne enheten ut av kiosk-modus (vedlikehold)?" + this.deviceSummary(d) +
                "Enheten låses opp ved neste synk.",
              okButton: "Avslutt kiosk-modus", cancelButton: "Avbryt"
            }
      );
      if (!ok) return;
      const returnPolicy = this.shortName(d.policyName || d.appliedPolicyName, "policies");
      MdmService.setKioskMode(d.id, enabled, returnPolicy).then(
        () => { this.loadDevices(true); },
        (error) => { this.deviceError = this.errMsg(error, "Kunne ikke endre kiosk-modus."); }
      );
    },
    /**
     * Force an app to update NOW: swap the device to a copy of its policy with the app BLOCKED (ADP
     * uninstalls it), wait for it to disappear from the device report, then swap back (ADP installs the
     * current release). This is the only mechanism AMAPI offers — Play's background updater has never
     * delivered a release to the fleet on its own.
     *
     * The uninstall WIPES the app's data, so the confirm text says so plainly. The terminal signs
     * itself back in afterwards — self-provisioning through Play Integrity, confirmed working in
     * production — so nobody has to stand at the device with a password. The notification-sound
     * settings do NOT survive it, though: they live in the app's own storage, which the uninstall
     * takes with everything else, so the confirm text says to set them up again.
     */
    async forceUpdate(d, packageName) {
      const ok = await this.$refs.confirmDialog.show({
        title: "Tving oppdatering",
        message:
          `Avinstallere og installere <code>${packageName}</code> på nytt på denne enheten?` + this.deviceSummary(d) +
          "Dette er eneste måten å tvinge en oppdatering ut umiddelbart. " +
          "<b>All appdata på enheten slettes</b> — inkludert den lokale databasen og eventuelle ordre som " +
          "ikke er synket opp. <b>Varslingslyd må settes opp på nytt</b>: valgt lyd per kanal, " +
          "varighet og volum følger ikke med over en reinstallasjon.<br><br>" +
          "<b>Terminalen logger seg inn selv etterpå.</b> Appen henter sin egen legitimasjon via Play " +
          "Integrity — bekreftet i produksjon. Gjør det likevel når terminalen er i ro: appen er borte " +
          "en stund mens den installeres på nytt.",
        okButton: "Tving oppdatering", cancelButton: "Avbryt"
      });
      if (!ok) return;

      this.forceUpdateBusy = d.id;
      this.deviceError = "";
      try {
        await MdmService.startForceUpdate(d.id, packageName);
        this.forceUpdateNote = `Avinstallerer ${packageName} … enheten flyttes tilbake så snart appen er borte.`;
        await this.awaitUninstallThenRestore(d.id);
        // Deliberately not "fullført": all we know at this point is that the app is gone and the device
        // is back on its own policy. Whether Play actually reinstalls is a separate question, and on
        // 2026-08-05 it did not — it sat at IN_PROGRESS and ended at INSTALLATION_FAILURE_REASON_UNKNOWN.
        this.forceUpdateNote =
          "Appen er avinstallert og enheten står tilbake på sin egen policy. " +
          "Play skal installere siste versjon — kontrollér i «Installerte apper» at den faktisk kommer. " +
          "Terminalen logger seg inn selv når appen er på plass — men varslingslyden må settes opp på nytt.";
        this.loadDevices(true);
      } catch (error) {
        this.deviceError = this.errMsg(error, "Kunne ikke tvinge oppdatering.");
      } finally {
        this.forceUpdateBusy = "";
      }
    },

    /**
     * Poll until the app is gone from applicationReports, then swap the policy back. Bounded: a device
     * that never reports the uninstall is left in the update policy on purpose, with a message telling
     * the operator to finish it — silently restoring would reinstall the SAME version and look like the
     * whole thing did nothing.
     */
    async awaitUninstallThenRestore(deviceId) {
      const maxAttempts = 40;      // ~10 minutes at 15s — devices report every few minutes
      const intervalMs = 15000;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, intervalMs));
        let status;
        try {
          status = (await MdmService.getForceUpdateStatus(deviceId)).data;
        } catch (e) {
          continue;   // transient — keep waiting rather than abandoning a half-done swap
        }
        if (status && status.pending === false) {
          return;     // someone else finished it
        }
        if (status && status.appInstalled === false) {
          await MdmService.finishForceUpdate(deviceId);
          return;
        }
      }
      throw new Error(
        "Enheten har ikke rapportert at appen er avinstallert ennå. Den står fortsatt i oppdateringspolicyen — " +
        "fullfør når enheten har rapportert."
      );
    },

    /** Click the active column to flip it; click another to sort by it ascending. */
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
    },
    /**
     * A machine name split after each «_» and «.», rendered with a <wbr> after every piece, so a column
     * can wrap POS_DESKTOP_MAINTENANCE or com.cagista.tastylauncher at a sensible place instead of
     * holding the whole table at its width. Copying the text still gives the name unchanged.
     */
    breakable(value) {
      return String(value || "").split(/(?<=[_.])/);
    },
    shortName(name, segment) {
      if (!name) return "";
      const marker = "/" + segment + "/";
      const i = name.indexOf(marker);
      return i >= 0 ? name.substring(i + marker.length) : name;
    },
    /**
     * Why a device is still on its _PROV policy, or null when it is not provisioning.
     *
     * `provisioningPending` comes from the backend and lists the install-then-hide packages that have not
     * reported INSTALLED. The reconciler will not move the device off _PROV until that list is empty, and
     * it has no timeout - so a package that cannot install on this hardware parks the device there for
     * good. "stuck" is only a hint that it has been waiting a while; the packages are the real answer.
     */
    provisioningWait(d) {
      if (!d) return null;
      const policy = this.shortName(d.appliedPolicyName || d.policyName, "policies") || "";
      if (!policy.endsWith("_PROV")) return null;
      const pending = Array.isArray(d.provisioningPending) ? d.provisioningPending : [];
      // Time spent PROVISIONING, so enrollmentTime - not lastSeen, which is fresh precisely when the
      // device is online and would have made an actively-stuck device look healthy.
      const enrolled = d.enrollmentTime ? new Date(d.enrollmentTime).getTime() : 0;
      return {
        packages: pending,
        steady: policy.slice(0, -"_PROV".length),
        // A normal swap is dominated by AMAPI status-report lag (measured 20-35 min), so an hour in is
        // where "slow" stops being a good explanation.
        stuck: pending.length > 0 && enrolled > 0 && (Date.now() - enrolled) > 3600000
      };
    },
    deviceModel(d) {
      const hw = d.hardwareInfo || {};
      return [hw.brand, hw.model].filter(Boolean).join(" ") || "Ukjent modell";
    },
    /**
     * Which device a confirm is about, the way its row shows it: name, merchant, model and id, policy and
     * last contact. The confirms used to name only the model and the AMAPI id — «SUNMI V3 (38eef041…)» —
     * which says nothing about whose till it is. Escaped, because ConfirmDialogue renders v-html.
     */
    deviceSummary(d) {
      const esc = (v) => TextUtil.escapeHtml(v);
      const labelStyle = "color:var(--bo-muted); padding:2px 16px 2px 0; white-space:nowrap; vertical-align:top";
      const row = (label, value) => `<tr><td style="${labelStyle}">${label}</td><td style="padding:2px 0">${value}</td></tr>`;
      const policy = this.shortName(d.appliedPolicyName || d.policyName, "policies");
      return '<table style="font-size:0.9rem; margin:12px 0">' +
        row("Navn", d.customName ? `<strong>${esc(d.customName)}</strong>` : '<em style="color:var(--bo-muted)">Uten navn</em>') +
        row("Merchant", d.enrollmentTokenData ? `<code>${esc(d.enrollmentTokenData)}</code>` : "—") +
        row("Modell", `${esc(this.deviceModel(d))} · <code>${esc(d.id)}</code>`) +
        row("Policy", policy ? `<code>${esc(policy)}</code>` : "—") +
        row("Siste kontakt", `${this.deviceOnline(d) ? "Online" : "Offline"} · ${esc(this.relativeTime(this.lastSeenMs(d)))}`) +
        "</table>";
    },
    stateClass(state) {
      switch (state) {
        case "ACTIVE": return "bg-success";
        case "PROVISIONING": return "bg-info";
        case "DISABLED": return "bg-secondary";
        case "DELETED": return "bg-danger";
        default: return "bg-secondary";
      }
    },
    // Norwegian date dd.mm.yyyy (e.g. 05.06.2025). Accepts "yyyy-mm-dd" (security patch) or a full ISO date.
    formatDate(value) {
      if (!value) return "—";
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
      if (m) return m[3] + "." + m[2] + "." + m[1];
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return value;
      const p = (n) => String(n).padStart(2, "0");
      return p(dt.getDate()) + "." + p(dt.getMonth() + 1) + "." + dt.getFullYear();
    },
    formatTime(iso) {
      if (!iso) return "—";
      const dt = new Date(iso);
      if (isNaN(dt.getTime())) return iso;
      const p = (n) => String(n).padStart(2, "0");
      return p(dt.getDate()) + "." + p(dt.getMonth() + 1) + "." + dt.getFullYear() +
             " " + p(dt.getHours()) + ":" + p(dt.getMinutes());
    },
    // Norwegian "time ago" for the last check-in (e.g. "3 minutter siden", "2 dager siden").
    relativeTime(iso) {
      if (!iso) return "—";
      const t = new Date(iso).getTime();
      if (isNaN(t)) return iso;
      const sec = Math.floor(Math.max(0, Date.now() - t) / 1000);
      if (sec < 60) return "akkurat nå";
      const min = Math.floor(sec / 60);
      if (min < 60) return min + (min === 1 ? " minutt" : " minutter") + " siden";
      const hrs = Math.floor(min / 60);
      if (hrs < 24) return hrs + (hrs === 1 ? " time" : " timer") + " siden";
      const days = Math.floor(hrs / 24);
      if (days < 30) return days + (days === 1 ? " dag" : " dager") + " siden";
      const months = Math.floor(days / 30);
      if (months < 12) return months + (months === 1 ? " måned" : " måneder") + " siden";
      return Math.floor(days / 365) + " år siden";
    },
    // AMAPI periodic check-in (fallback signal): the newer of status report and policy sync, in ms (0 if never).
    // AMAPI reports PERIODICALLY (not a live heartbeat), so alone this is only a rough "last check-in".
    /**
     * When the device last sent its app inventory. Deliberately NOT lastSeenMs or amapiLastSeenMs:
     * those prefer the order-sync heartbeat and fold in lastPolicySyncTime, either of which can be
     * minutes old while applicationReports is an hour old — which would make the freshness note lie
     * in exactly the case it exists to explain.
     */
    appsReportedMs(d) {
      return d && d.lastStatusReportTime ? new Date(d.lastStatusReportTime).getTime() : 0;
    },
    amapiLastSeenMs(d) {
      const a = d.lastStatusReportTime ? new Date(d.lastStatusReportTime).getTime() : 0;
      const b = d.lastPolicySyncTime ? new Date(d.lastPolicySyncTime).getTime() : 0;
      return Math.max(a || 0, b || 0);
    },
    // Best "last seen" for display: the real-time order-sync heartbeat when present, else the AMAPI check-in.
    lastSeenMs(d) {
      return d.lastHeartbeatMs > 0 ? d.lastHeartbeatMs : this.amapiLastSeenMs(d);
    },
    // Online = a fresh order-sync heartbeat (~30s cadence, accurate) when the device sends one; otherwise
    // fall back to the AMAPI check-in window (approximate) for devices whose app doesn't heartbeat yet.
    deviceOnline(d) {
      if (d.lastHeartbeatMs > 0) {
        return Date.now() - d.lastHeartbeatMs <= this.heartbeatOnlineSeconds * 1000;
      }
      const t = this.amapiLastSeenMs(d);
      return t > 0 && Date.now() - t <= this.onlineThresholdMinutes * 60 * 1000;
    },
    // Open the fixed-position actions menu under the clicked ⋮ button (or toggle it shut).
    toggleMenu(d, event) {
      if (this.openMenu && this.openMenu.device.id === d.id) { this.openMenu = null; return; }
      const r = event.currentTarget.getBoundingClientRect();
      this.openMenu = { device: d, top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) };
    },
    /**
     * Running against the shared backend from a developer machine.
     *
     * Backoffice talks to the SAME CagistaServices and the SAME AMAPI enterprise in dev as in production,
     * so a click here reaches a real till in a real restaurant. Judged by hostname rather than by build
     * type: a production build served from localhost is still a developer sitting in front of it.
     */
    isLocalDev,
    /** A device the fleet actually depends on, marked by PROD in the name it was given at staging. */
    isProdDevice(d) {
      return !!(d && d.customName && d.customName.toUpperCase().includes("PROD"));
    },
    /**
     * Read-only from here. NOT a security boundary - the backend is shared and cannot tell a developer's
     * browser from the real console, so anyone determined can bypass it. It exists to stop the accident:
     * wiping or re-policying a live till because the tab looked like the dev one.
     *
     * KEEP THIS, permanently. A separate dev AMAPI enterprise is planned
     * (CAGISTA/DeviceManagement_MDM_Strategy.md) and will remove the usual way to reach production from a
     * developer machine — but not every way: a dev Backoffice can still be pointed at production config,
     * and environments get crossed. The guard costs nothing when it is not needed, and the one time it is,
     * it is standing between a stray click and a restaurant. Do not delete it as obsolete once dev has its
     * own enterprise.
     */
    isDeviceLocked(d) {
      return this.isLocalDev() && this.isProdDevice(d);
    },
    /**
     * Refuses and says why. Returns true when the caller must stop.
     *
     * Uses the app's own dialogue rather than window.alert, and not only to match the rest of the UI:
     * a native alert carries a "don't allow this site to prompt you again" checkbox, and once someone
     * ticks it the refusal becomes silent. The action would still be blocked, but the click would do
     * nothing at all with no explanation — which is how a guard gets mistaken for a bug.
     */
    async refuseIfLocked(d) {
      if (!this.isDeviceLocked(d)) return false;
      await this.$refs.confirmDialog.show({
        title: "Skrivebeskyttet PROD-enhet",
        message:
          `<code>${d.customName || this.deviceModel(d)}</code> er merket <strong>PROD</strong>, og ` +
          "Backoffice kjører fra <code>localhost</code> mot samme AMAPI-enterprise som produksjon." +
          "<br><br>Du kan se AMAPI-data, men ikke endre, kommandere eller slette enheten herfra. " +
          "Bruk produksjons-Backoffice hvis endringen er tilsiktet.",
        okButton: "Lukk"
      });
      return true;
    },
    async menuAction(kind) {
      const d = this.openMenu && this.openMenu.device;
      this.openMenu = null;
      if (!d) return;
      // Allowlist, not a blocklist: anything NOT named here is treated as modifying, so an action added
      // later is refused by default rather than quietly reaching a production device.
      const readOnly = ["details", "apps", "raw"];
      if (!readOnly.includes(kind) && await this.refuseIfLocked(d)) return;
      if (kind === "remote") this.startRemoteControl(d);
      else if (kind === "rustdeskId") this.promptRustdeskId(d);
      else if (kind === "kioskOff") this.kioskMode(d, false);
      else if (kind === "kioskOn") this.kioskMode(d, true);
      else if (kind === "details") this.showDetails(d);
      else if (kind === "apps") this.showApps(d);
      else if (kind === "raw") this.showRaw(d, this.deviceModel(d) + " (" + d.id + ")");
      else if (kind === "reboot") this.confirmCommand(d, "REBOOT");
      else if (kind === "lock") this.confirmCommand(d, "LOCK");
      else if (kind === "wipe") this.confirmWipe(d);
      else if (kind === "rma") this.startRma(d);
      else if (kind.startsWith("forceUpdate:")) this.forceUpdate(d, kind.substring("forceUpdate:".length));
    },

    /**
     * The CAGISTA apps on a device that a forced update may target, with the version each runs now.
     * Excludes the launcher — uninstalling the home app would leave the device with nothing handling
     * HOME, and the server rejects it anyway.
     */
    /**
     * The CAGISTA apps this device's POLICY installs — not the ones its last report happened to mention.
     *
     * <p>It used to read applicationReports and require {@code state === "INSTALLED"}, which meant the
     * action vanished for an app the device had not yet reported as back: after a forced update on
     * 2026-08-05 «Tving oppdatering: Backoffice» disappeared from the menu while the app was in fact
     * running on the device. A report is a snapshot from the last check-in, sometimes an hour old — and
     * the «Siste kontakt» column does not warn you, because it prefers the order-sync heartbeat, which
     * can be seconds old at the same time.
     *
     * <p>The policy is the right source: it says what the device is supposed to have, which is exactly
     * what "force this app to update" needs to know, and it does not go stale between check-ins.
     *
     * <p>The report is still consulted, but only to decorate — display name and the version currently
     * known. Anything missing degrades to the package name and no version, which also means this keeps
     * working if applicationReports is ever dropped from the device-list payload (it is large: a few
     * hundred entries per device, and at thousands of devices it is the thing that will break the list
     * long before this menu does).
     */
    updatableApps(d) {
      const policy = this.shortName(d && (d.appliedPolicyName || d.policyName), "policies");
      const packages = this.policyApps[policy] || [];
      const reports = (d && Array.isArray(d.applicationReports)) ? d.applicationReports : [];
      return packages.map((pkg) => {
        const report = reports.find((a) => a.packageName === pkg);
        return {
          packageName: pkg,
          displayName: (report && report.displayName) || pkg,
          versionCode: (report && report.state === "INSTALLED" && report.versionCode) || null
        };
      });
    },
    // Remote control is offered ONLY for the configured (non-payment) policy roles — a SoftPOS/payment device
    // must never be silently remote-controllable. Fail-closed: unknown policy or empty allowlist -> false.
    remoteAllowed(d) {
      if (!d || !this.remoteControl.enabled) return false;
      const roles = this.remoteControl.allowedRoles || [];
      const policy = this.shortName(d.appliedPolicyName || d.policyName, "policies");
      return !!policy && roles.includes(policy);
    },
    // Open a RustDesk remote session to the device. The stock RustDesk client AUTO-GENERATES its ID (no
    // deterministic/custom ID; Android managed configuration is upstream-wontfix, rustdesk#4319), so the ID
    // is recorded per device at staging and substituted as {id} in the connect template. {serial} is also
    // substituted for a future deterministic-ID client (e.g. a Pro custom build).
    startRemoteControl(d) {
      if (!this.remoteAllowed(d)) return;
      let id = (d.rustdeskId || "").trim();
      if (!id) {
        id = this.promptRustdeskId(d);
        if (!id) return;
      }
      const serial = (d.hardwareInfo && d.hardwareInfo.serialNumber) || "";
      const url = (this.remoteControl.connectUrlTemplate || "")
        .replace("{id}", encodeURIComponent(id))
        .replace("{serial}", encodeURIComponent(serial));
      if (url) window.open(url, "_blank");
    },
    // Prompt-and-save the device's RustDesk ID (shown on the device: open the RustDesk app at staging and
    // read the 9-digit ID). Blank clears. Returns the saved ID ("" when cancelled/cleared).
    promptRustdeskId(d) {
      const value = window.prompt(
        "RustDesk-ID for denne enheten (åpne RustDesk-appen på enheten og les av ID-en). Tom = fjern.",
        (d.rustdeskId || "").trim()
      );
      if (value === null) return "";
      const id = value.replace(/\s/g, "");
      MdmService.setRustdeskId(d.id, id).then(
        () => { d.rustdeskId = id; },
        (error) => {
          const data = error && error.response ? error.response.data : null;
          this.deviceError = (data && data.error) || "Kunne ikke lagre RustDesk-ID.";
        }
      );
      return id;
    },
    // Inline rename of a device's user-defined name (stored server-side; AMAPI has no label field).
    async startRename(d) {
      if (await this.refuseIfLocked(d)) return;
      this.editNameId = d.id;
      this.editNameValue = d.customName || "";
    },
    saveName(d) {
      const name = this.editNameValue.trim();
      MdmService.setDeviceName(d.id, name).then(
        () => { d.customName = name; this.editNameId = null; },
        (error) => { console.log(error); this.editNameId = null; }
      );
    },
    // Load the policy kinds (name -> kind) first — kiosk-off detection + the reassign dropdown both need them —
    // then build the reassignable-policy list.
    loadPolicyOptions() {
      MdmService.getPolicySettings().then(
        (res) => { this.policyKinds = (res.data && res.data.kinds) || {}; this.buildPolicyOptions(); },
        (error) => { console.log(error); this.buildPolicyOptions(); }
      );
    },
    // The policies a device can be reassigned to. Excludes every MAINTENANCE-kind policy (a device
    // enters/leaves maintenance via the kiosk action, not by reassignment here) and both system-generated
    // variants: *_PROV, and *_UPDATE — a copy with one app deliberately omitted so ADP uninstalls it.
    // Assigning a device to one by hand would strip the app and leave it stripped: the move back happens
    // through the «Tving oppdatering» flow's own bookkeeping, not because the policy says so.
    buildPolicyOptions() {
      MdmService.listPolicies().then(
        (response) => {
          const list = (response.data && response.data.policies) || [];
          this.policyOptions = [...new Set(list.map((p) => this.shortName(p.name, "policies")).filter(Boolean))]
            .filter((p) => !p.endsWith("_PROV") && !p.endsWith("_UPDATE") && this.policyKinds[p] !== "MAINTENANCE");
          // Which CAGISTA apps each policy puts on a device — the source of truth for the force-update
          // menu (see updatableApps). Built here because the policies are already being fetched: there is
          // a handful of them regardless of fleet size, and they are cached for the page's lifetime.
          const apps = {};
          const packages = {};
          list.forEach((p) => {
            const short = this.shortName(p.name, "policies");
            if (!short) return;
            const all = (Array.isArray(p.applications) ? p.applications : [])
              .map((a) => (a && a.packageName) || "")
              .filter(Boolean);
            packages[short] = all;
            apps[short] = all.filter((pkg) => pkg.startsWith("com.cagista.") && pkg !== "com.cagista.tastylauncher");
          });
          this.policyApps = apps;
          this.policyPackages = packages;
        },
        (error) => { console.log(error); }
      );
    },
    async startEditPolicy(d) {
      if (await this.refuseIfLocked(d)) return;
      this.editPolicyId = d.id;
      this.editPolicyValue = this.shortName(d.appliedPolicyName || d.policyName, "policies") || (this.policyOptions[0] || "");
    },
    savePolicy(d) {
      const policyId = this.editPolicyValue;
      if (!policyId) { this.editPolicyId = null; return; }
      MdmService.setDevicePolicy(d.id, policyId).then(
        () => { d.policyName = policyId; d.appliedPolicyName = policyId; this.editPolicyId = null; },
        (error) => { this.deviceError = this.errMsg(error, "Kunne ikke endre policy."); this.editPolicyId = null; }
      );
    },
    // reset=true → fresh first page; reset=false → append the next page via the page token.
    loadDevices(reset = true) {
      this.devicesLoading = true;
      this.deviceError = "";
      if (reset) { this.devices = []; this.devicePageToken = null; }
      MdmService.listDevices(this.devicePageSize, reset ? null : this.devicePageToken).then(
        (response) => {
          const list = Array.isArray(response.data.devices) ? response.data.devices : [];
          const mapped = list.map((d) => ({ ...d, id: this.shortName(d.name, "devices") }));
          this.devices = reset ? mapped : this.devices.concat(mapped);
          this.devicePageToken = response.data.nextPageToken || null;
          this.devicesLoading = false;
        },
        (error) => {
          this.devicesLoading = false;
          this.deviceError = this.errMsg(error, "Kunne ikke hente enheter.");
        }
      );
    },
    loadMoreDevices() {
      if (this.devicePageToken) this.loadDevices(false);
    },
    loadQuota() {
      MdmService.getQuotaStatus().then(
        (response) => { this.quota = response.data; },
        (error) => { console.log(error); }
      );
    },
    async confirmCommand(d, type) {
      const labels = { REBOOT: "Reboot", LOCK: "Slå av skjerm" };
      const ok = await this.$refs.confirmDialog.show({
        title: labels[type],
        message: `Vil du sende «${labels[type]}» til denne enheten?` + this.deviceSummary(d),
        okButton: "Ja", cancelButton: "Avbryt"
      });
      if (!ok) return;
      MdmService.deviceCommand(d.id, type).then(
        () => {},
        (error) => { this.deviceError = this.errMsg(error, "Kommandoen feilet."); }
      );
    },
    // Mark a device as RMA — a NOTE only. The device is NOT deleted from AMAPI (unlike wipe).
    startRma(d) {
      this.rmaDevice = d;
      this.rmaNote = (d.rma && d.rma.note) || "";
      this.$refs.rmaModal.open();
    },
    saveRma() {
      const d = this.rmaDevice;
      if (!d) return;
      this.rmaSaving = true;
      MdmService.setDeviceRma(d.id, true, this.rmaNote).then(
        () => { d.rma = { note: this.rmaNote.trim(), at: new Date().toISOString() }; this.rmaSaving = false; this.$refs.rmaModal.close(); },
        (error) => { this.rmaSaving = false; this.deviceError = this.errMsg(error, "Kunne ikke merke som RMA."); }
      );
    },
    clearRma() {
      const d = this.rmaDevice;
      if (!d) return;
      this.rmaSaving = true;
      MdmService.setDeviceRma(d.id, false, "").then(
        () => { d.rma = null; this.rmaSaving = false; this.$refs.rmaModal.close(); },
        (error) => { this.rmaSaving = false; this.deviceError = this.errMsg(error, "Kunne ikke fjerne RMA-merking."); }
      );
    },
    async confirmWipe(d) {
      const ok = await this.$refs.confirmDialog.show({
        title: "Fabrikkgjenoppretting",
        message: `<strong>Dette nullstiller denne enheten til fabrikkinnstillinger:</strong>` + this.deviceSummary(d) +
          `All data slettes (inkl. eksternt lager), og enheten fjernes fra administrasjon og må re-provisjoneres. ` +
          `Dette kan IKKE angres. Fortsette?`,
        okButton: "Wipe enhet", cancelButton: "Avbryt"
      });
      if (!ok) return;
      MdmService.wipeDevice(d.id).then(
        () => { this.devices = this.devices.filter((x) => x.id !== d.id); },
        (error) => { this.deviceError = this.errMsg(error, "Wipe feilet."); }
      );
    },
    // Read-only raw-JSON inspect (the device object is the AMAPI response from devices.list).
    // Formatted device details from the existing device object (no extra AMAPI call).
    showDetails(d) {
      this.detailsDevice = d;
      this.detailsTitle = this.deviceModel(d) + " (" + d.id + ")";
      this.$refs.detailsModal.open();
    },
    hw(k) { return (this.detailsDevice && this.detailsDevice.hardwareInfo && this.detailsDevice.hardwareInfo[k]) || ""; },
    sw(k) { return (this.detailsDevice && this.detailsDevice.softwareInfo && this.detailsDevice.softwareInfo[k]) || ""; },
    net(k) { return (this.detailsDevice && this.detailsDevice.networkInfo && this.detailsDevice.networkInfo[k]) || ""; },
    mem(k) { return (this.detailsDevice && this.detailsDevice.memoryInfo && this.detailsDevice.memoryInfo[k]) || ""; },
    formatBytes(n) {
      const b = Number(n);
      if (!b || isNaN(b)) return "—";
      const gb = b / (1024 * 1024 * 1024);
      if (gb >= 1) return gb.toFixed(1).replace(".", ",") + " GB";
      return Math.round(b / (1024 * 1024)) + " MB";
    },
    // Installed-apps modal from the device's AMAPI applicationReports[] (no extra call; the policy
    // has applicationReportsEnabled, so devices report their app inventory on each check-in).
    showApps(d) {
      this.appsDevice = d;
      this.appsTitle = this.deviceModel(d) + " (" + d.id + ")";
      this.appsSearch = "";
      this.appsShowSystem = false;
      this.$refs.appsModal.open();
    },
    appSourceLabel(s) {
      const m = {
        SYSTEM_APP_FACTORY_VERSION: "Systemapp",
        SYSTEM_APP_UPDATED_VERSION: "Systemapp (oppdatert)",
        INSTALLED_FROM_PLAY_STORE: "Managed Play"
      };
      return m[s] || "—";
    },
    appsToRaw() {
      const reports = (this.appsDevice && this.appsDevice.applicationReports) || [];
      const t = this.appsTitle + " — applicationReports";
      this.$refs.appsModal.close();
      this.showRaw(reports, t);
    },
    detailsToRaw() {
      const d = this.detailsDevice;
      const t = this.detailsTitle;
      this.$refs.detailsModal.close();
      this.showRaw(d, t);
    },
    showRaw(obj, title) {
      this.rawTitle = title || "";
      this.rawJson = JSON.stringify(obj, null, 2);
      this.copied = false;
      this.$refs.rawModal.open();
    },
    copyRaw() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.rawJson).then(() => { this.copied = true; }).catch(() => {});
      }
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
.mdm-th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.mdm-th-sort:hover {
  color: var(--bo-ink, #212529);
}
.mdm-sort-caret {
  font-size: 0.75em;
  margin-left: 4px;
  opacity: 0.35;
}
.mdm-sort-caret--active {
  opacity: 1;
  color: #ECB109;
}

.mdm-enterprise {
  color: var(--bo-muted);
  font-size: 0.9rem;
  white-space: nowrap;
}
.mdm-device-id {
  font-size: 0.78rem;
  color: var(--bo-muted-2);
}

/* Device-quota meter */
.mdm-quota {
  max-width: 560px;
}
.mdm-quota__label {
  font-weight: 600;
  color: var(--bo-ink);
}
.mdm-quota__source {
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
}
.mdm-quota__source--live { background: var(--bo-ok-bg); color: #1e7e34; }
.mdm-quota__count {
  color: var(--bo-ink);
}
.mdm-quota__bar {
  height: 10px;
  background: var(--bo-surface-2);
  border-radius: 6px;
  overflow: hidden;
}
.mdm-quota__fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s ease;
}
.mdm-quota__fill--ok { background: #2E7D32; }
.mdm-quota__fill--warn { background: #ECB109; }
.mdm-quota__fill--danger { background: #c0392b; }

/* MDM summary cards */
.mdm-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.mdm-stat {
  flex: 1 1 130px;
  min-width: 130px;
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-top: 3px solid #ECB109;
  border-radius: 10px;
  padding: 0.85rem 1.1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.mdm-stat--active { border-top-color: #2E7D32; }
.mdm-stat--prov { border-top-color: #0d6efd; }
.mdm-stat--disabled { border-top-color: #adb5bd; }
.mdm-stat__value {
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--bo-ink);
  line-height: 1.1;
}
.mdm-stat__label {
  font-size: 0.76rem;
  color: var(--bo-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 2px;
}

/* Compact icon buttons for row actions (delete on the left, others on the right). */
.mdm-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--bo-line);
  background: var(--bo-surface);
  color: var(--bo-ink-2);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mdm-icon-btn:hover {
  background: var(--bo-surface-2);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.mdm-icon-btn--danger {
  color: var(--bo-alert-ink);
  border-color: #e7c3bd;
}
.mdm-icon-btn--danger:hover {
  background: var(--bo-bad-bg);
}

/* Online/offline traffic light (inferred from the last AMAPI check-in — no live presence signal exists).
   Green = online, red = offline. */
.mdm-prov {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
  font-size: 0.78rem;
  color: var(--bo-brand-ink);
}
.mdm-prov--stuck { color: #b00020; }
.mdm-prov__pkg { margin-left: 4px; }

.mdm-devbanner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 680px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-style: normal;
  color: var(--bo-brand-ink);
  background: var(--bo-brand-tint);
  border: 1px solid #ffd591;
}

/* A device this browser must not touch. Amber rather than red: nothing is wrong, it is simply not
   this console's to change. */
.mdm-prodlock {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  flex-shrink: 0;   /* a card squeezed it to «PRO/D» */
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  color: var(--bo-brand-ink);
  background: var(--bo-brand-tint);
  border: 1px solid #ffd591;
}

.mdm-light { font-size: 0.8rem; }
.mdm-light--online { color: var(--bo-success-ink); }
.mdm-light--offline { color: #d32f2f; }
/* The actions column is as wide as its button and no wider. */
.mdm-devices .mdm-col-actions { width: 1%; }
/* Keep the Online column as narrow as its content (shrink-to-fit + tight padding) */
.mdm-col-online {
  width: 1%;
  white-space: nowrap;
  padding-left: 6px;
  padding-right: 6px;
}

/* User-defined device name: subtle rename pencil that surfaces on row hover */
.mdm-name-edit {
  border: none;
  background: none;
  color: #adb5bd;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}
tr:hover .mdm-name-edit { opacity: 1; }
/* No hover, no pencil — it was invisible AND the only way to rename a device from a touch terminal. */
@media (pointer: coarse) { .mdm-name-edit { opacity: 1; } }
.mdm-name-edit:hover { color: var(--bo-brand-ink); }

/* Row-actions dropdown (fixed-positioned so the table's overflow never clips it) */
.mdm-actions-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1040;
}
.mdm-actions-portal {
  position: fixed;
  z-index: 1050;
  min-width: 12rem;
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.16);
  padding: 4px 0;
  /* The menu grows with the device's app count, so it can outgrow the window. Bound it to the space
     below the trigger and let it scroll — otherwise the last entries are simply unreachable. */
  overflow-y: auto;
  overscroll-behavior: contain;
}
.mdm-actions__item {
  display: block;
  padding: 8px 16px;
  font-size: 0.9rem;
  color: var(--bo-ink);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
}
.mdm-actions__item:hover { background: var(--bo-surface-2); }
.mdm-actions__item--danger { color: var(--bo-alert-ink); }
.mdm-actions__item--danger:hover { background: var(--bo-bad-bg); }
.mdm-actions__divider {
  height: 1px;
  background: var(--bo-line);
  margin: 4px 0;
}

/* Formatted device-details modal */
.mdm-details {
  font-size: 0.88rem;
}
.mdm-details__section {
  font-weight: 600;
  color: var(--bo-ink);
  margin-top: 0.85rem;
  margin-bottom: 0.25rem;
  padding-bottom: 2px;
  border-bottom: 1px solid var(--bo-line);
}
.mdm-details__section:first-child {
  margin-top: 0;
}
.mdm-details__row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 2px 10px 2px 0;
}
.mdm-details__row > span:first-child {
  color: var(--bo-muted);
}
.mdm-details__row > span:last-child {
  text-align: right;
  word-break: break-word;
}

/* Raw-JSON inspect block */
.mdm-raw {
  background: var(--bo-surface-2);
  border: 1px solid var(--bo-line);
  color: #24292e;
  padding: 0.75rem;
  border-radius: 8px;
  max-height: 60vh;
  overflow: auto;
  font-size: 0.78rem;
  white-space: pre;
  margin: 0;
}
/* Widen ONLY the installed-apps modal so all 4 columns fit on desktop; other modals on this page keep
   PopupModal's 480px default. :has() targets just this modal's .window (the .popup-modal root carries this
   page's scope attr; .window is a descendant of it). On narrow screens width:100% shrinks it responsively. */
:deep(.window:has(.mdm-apps-modal)) {
  max-width: 960px;
  width: 100%;
}
/* Installed-apps modal table. */
.mdm-apps-table {
  font-size: 0.85rem;
  margin-bottom: 0;
}
.mdm-apps-table thead th {
  position: sticky;
  top: 0;
  background: var(--bo-surface);
  z-index: 1;
  font-size: 0.78rem;
  color: var(--bo-muted);
  font-weight: 600;
}
/* CAGISTA fleet-app badge (amber brand marker, matches bo_btn family). */
.mdm-app-ours {
  display: inline-block;
  background: #ECB109;
  color: var(--bo-ink);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 1px 5px;
  border-radius: 4px;
  vertical-align: middle;
  margin-right: 6px;
}

/* ============================ Cards below 1200px ============================
   The ten-column table needs 1131px (measured in headless Chrome against the fleet's rows plus a
   provisioning device), which the page has from a 1200px window up. Narrower, `table-responsive` would
   only turn it into a sideways scroll, so reaching the actions button means dragging past columns you
   did not want. There the SAME markup reflows into one card per device — no second template to keep in
   sync, and no risk of the two drifting. Each cell prints its own heading from data-label, since the
   header row is gone. */
@media (max-width: 1199.98px) {
  .mdm-devices thead {
    display: none;
  }
  .mdm-devices,
  .mdm-devices tbody,
  .mdm-devices tr,
  .mdm-devices td {
    display: block;
    width: 100%;
  }
  .mdm-devices tr {
    position: relative;
    border: 1px solid var(--bo-line);
    border-radius: 10px;
    margin-bottom: 0.75rem;
    padding: 0.6rem 0.75rem 0.5rem;
    background: var(--bo-surface);
  }
  /* Bootstrap's table styling draws a line under every cell; as cards those become stripes. */
  /* overflow-wrap: anywhere is the safety net: a value with no break in it (a package name, a model
     code) wraps inside the card instead of pushing the card, and the page, wider than the screen. */
  .mdm-devices td {
    border: none !important;
    padding: 0.2rem 0 !important;
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    text-align: left !important;
    overflow-wrap: anywhere;
  }
  .mdm-devices td::before {
    content: attr(data-label);
    flex: 0 0 7.5rem;
    color: var(--bo-muted);
    font-size: 0.78rem;
    font-weight: 600;
  }

  /* Online light and name form the card's title row: the light inline before the name, both bigger than
     the rest, so a scan reads "which device, is it up" before any detail. */
  .mdm-devices td.mdm-col-online {
    position: absolute;
    top: 0.7rem;
    right: 0.75rem;
    width: auto;
    padding: 0 !important;
  }
  .mdm-devices td.mdm-col-online::before {
    content: none;
  }
  .mdm-devices td[data-label="Navn"] {
    font-size: 1.02rem;
    font-weight: 600;
    padding-right: 4.5rem !important;   /* clear of the actions button */
    margin-bottom: 0.35rem;
  }
  .mdm-devices td[data-label="Navn"]::before {
    content: none;
  }

  /* The actions button sits in the card's corner rather than at the end of a column. */
  .mdm-devices td.mdm-col-actions {
    position: absolute;
    top: 1.7rem;
    right: 0.5rem;
    width: auto;
    padding: 0 !important;
  }
  .mdm-devices td.mdm-col-actions::before {
    content: none;
  }

  /* The provisioning notice is a block, not an inline value — it must not sit on the label's baseline. */
  .mdm-devices td[data-label="Policy"] {
    flex-wrap: wrap;
  }
  .mdm-devices .mdm-prov {
    flex: 1 1 100%;
  }
}

/* Two cards across from 992px: one card per row would be a 900px stripe with its values bunched at the
   left. Not below — a card under 400px cannot hold a long model name beside the device id.
   align-items: start keeps a short card from stretching to its taller neighbour. */
@media (min-width: 992px) and (max-width: 1199.98px) {
  .mdm-devices tbody {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    align-items: start;
  }
  .mdm-devices tr {
    margin-bottom: 0;
  }
}

/* ============================ Phone ============================ */
@media (max-width: 767.98px) {
  /* Stat cards two-up instead of four across a phone, which would leave them unreadably narrow. */
  .mdm-stat {
    flex: 1 1 calc(50% - 0.5rem);
    min-width: 0;
    padding: 0.7rem 0.8rem;
  }

  /* The filter row: full-width controls stacked, rather than three things squeezed onto one line. */
  .mdm-filters > * {
    flex: 1 1 100% !important;
    max-width: none !important;
  }
  .mdm-filters .bo_btn_secondary {
    margin-left: 0 !important;
  }

  /* The header actions stop hugging the right edge, which on a phone puts them under the title. */
  .mdm-header-actions {
    align-items: stretch !important;
    width: 100%;
  }
  .mdm-header-actions .bo_btn,
  .mdm-header-actions .bo_btn_secondary {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* Wider than a phone is tall would push the page sideways. */
  .mdm-actions-portal {
    max-width: calc(100vw - 1rem);
  }
  /* Menu entries are tapped, not clicked with a pointer — give them a finger-sized row. */
  .mdm-actions__item {
    padding-top: 0.6rem !important;
    padding-bottom: 0.6rem !important;
  }

  /* 34px is under the 44px touch minimum, and on a phone this button is the whole point of the card. */
  .mdm-devices td.mdm-col-actions .mdm-icon-btn {
    width: 44px;
    height: 44px;
  }

  /* The rename/policy pencils are opacity:0 until `tr:hover`, and a touch screen has no hover — so on a
     phone they were invisible AND unreachable. Show them, and make them a real tap target. */
  .mdm-devices .mdm-name-edit {
    opacity: 1;
    padding: 0.5rem;
  }
}
</style>
